import React, { useState, useEffect } from 'react';
import { 
  searchTaxForms, 
  searchWriteoffs, 
  searchBusinessDocuments,
  getPopularTaxForms,
  getPopularWriteoffs,
  getBusinessDocuments
} from '../lib/api';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Search, 
  FileText, 
  DollarSign, 
  X, 
  Info, 
  FolderOpen,
  Star,
  TrendingUp,
  Building2,
  FileCheck
} from 'lucide-react';

export default function TaxFormsSearch(props) {
  const onSelectForm = props.onSelectForm;
  const onSelectWriteoff = props.onSelectWriteoff;
  const onSelectDocument = props.onSelectDocument;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [formResults, setFormResults] = useState([]);
  const [writeoffResults, setWriteoffResults] = useState([]);
  const [documentResults, setDocumentResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  
  // Popular items for initial display
  const [popularForms, setPopularForms] = useState([]);
  const [popularWriteoffs, setPopularWriteoffs] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(true);

  // Load popular items on mount
  useEffect(function loadPopular() {
    async function fetchPopular() {
      try {
        const [formsRes, writeoffsRes, docsRes] = await Promise.all([
          getPopularTaxForms(),
          getPopularWriteoffs(),
          getBusinessDocuments()
        ]);
        setPopularForms(formsRes.data || []);
        setPopularWriteoffs(writeoffsRes.data || []);
        setAllDocuments(docsRes.data || []);
      } catch (error) {
        console.error('Failed to load popular items:', error);
      } finally {
        setLoadingPopular(false);
      }
    }
    fetchPopular();
  }, []);

  useEffect(function searchEffect() {
    if (searchQuery.length < 2) {
      setFormResults([]);
      setWriteoffResults([]);
      setDocumentResults([]);
      return;
    }

    setLoading(true);
    const timer = setTimeout(async function() {
      try {
        const [formRes, writeoffRes, docRes] = await Promise.all([
          searchTaxForms(searchQuery),
          searchWriteoffs(searchQuery),
          searchBusinessDocuments(searchQuery)
        ]);
        setFormResults(formRes.data || []);
        setWriteoffResults(writeoffRes.data?.writeoffs || []);
        setDocumentResults(docRes.data || []);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
    
    return function() { clearTimeout(timer); };
  }, [searchQuery]);

  function handleClear() {
    setSearchQuery('');
  }

  function renderFormItem(form, index) {
    return (
      <Card 
        key={form.id || index}
        className="cursor-pointer hover:border-[hsl(var(--accent))] transition-colors"
        onClick={function() { if (onSelectForm) onSelectForm(form); }}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[rgba(20,184,166,0.1)]">
            <FileText className="w-5 h-5 text-[hsl(var(--accent))]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{form.name}</span>
              {form.category && (
                <Badge variant="outline" className="text-xs capitalize">
                  {form.category.replace('_', ' ')}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground truncate">{form.full_name}</p>
            {form.deadline && (
              <p className="text-xs text-muted-foreground mt-1">Deadline: {form.deadline}</p>
            )}
          </div>
          <Info className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </CardContent>
      </Card>
    );
  }

  function renderWriteoffItem(writeoff, index) {
    return (
      <Card 
        key={index}
        className="cursor-pointer hover:border-[hsl(var(--accent))] transition-colors"
        onClick={function() { if (onSelectWriteoff) onSelectWriteoff(writeoff); }}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[rgba(34,197,94,0.1)]">
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{writeoff.name}</span>
              {writeoff.avg_deduction ? (
                <Badge variant="outline" className="text-green-500 border-green-500/30">
                  ~${writeoff.avg_deduction.toLocaleString()}
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground truncate">{writeoff.description}</p>
            {writeoff.schedule_c_line && (
              <p className="text-xs text-muted-foreground mt-1">Schedule C Line: {writeoff.schedule_c_line}</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderDocumentItem(doc, index) {
    return (
      <Card 
        key={index}
        className="cursor-pointer hover:border-[hsl(var(--accent))] transition-colors"
        onClick={function() { if (onSelectDocument) onSelectDocument(doc); }}
      >
        <CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[rgba(59,130,246,0.1)]">
            <FolderOpen className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold">{doc.name}</span>
              <Badge variant="outline" className="text-xs capitalize text-blue-400 border-blue-400/30">
                {doc.category.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground truncate">{doc.description}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalResults = formResults.length + writeoffResults.length + documentResults.length;
  const hasSearchQuery = searchQuery.length >= 2;
  
  // Build result elements
  const formElements = [];
  const formsToShow = hasSearchQuery ? formResults : popularForms;
  for (let i = 0; i < formsToShow.length; i++) {
    formElements.push(renderFormItem(formsToShow[i], i));
  }
  
  const writeoffElements = [];
  const writeoffsToShow = hasSearchQuery ? writeoffResults : popularWriteoffs;
  for (let i = 0; i < writeoffsToShow.length; i++) {
    writeoffElements.push(renderWriteoffItem(writeoffsToShow[i], i));
  }
  
  const documentElements = [];
  const docsToShow = hasSearchQuery ? documentResults : allDocuments;
  for (let i = 0; i < docsToShow.length; i++) {
    documentElements.push(renderDocumentItem(docsToShow[i], i));
  }

  return (
    <div className="space-y-4" data-testid="tax-forms-search">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search tax forms, write-offs, business documents..."
          value={searchQuery}
          onChange={function(e) { setSearchQuery(e.target.value); }}
          className="pl-10 pr-10"
          data-testid="tax-search-input"
        />
        {searchQuery ? (
          <button
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={handleClear}
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>

      {/* Tabs for organizing results */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all" className="text-xs sm:text-sm">
            All {hasSearchQuery && totalResults > 0 && `(${totalResults})`}
          </TabsTrigger>
          <TabsTrigger value="forms" className="text-xs sm:text-sm">
            <FileText className="w-3 h-3 mr-1 hidden sm:inline" />
            Forms {formElements.length > 0 && `(${formElements.length})`}
          </TabsTrigger>
          <TabsTrigger value="writeoffs" className="text-xs sm:text-sm">
            <DollarSign className="w-3 h-3 mr-1 hidden sm:inline" />
            Write-Offs {writeoffElements.length > 0 && `(${writeoffElements.length})`}
          </TabsTrigger>
          <TabsTrigger value="documents" className="text-xs sm:text-sm">
            <FolderOpen className="w-3 h-3 mr-1 hidden sm:inline" />
            Docs {documentElements.length > 0 && `(${documentElements.length})`}
          </TabsTrigger>
        </TabsList>

        {loading || loadingPopular ? (
          <div className="text-center py-8 text-muted-foreground">
            {loading ? 'Searching...' : 'Loading...'}
          </div>
        ) : (
          <>
            {/* All Tab */}
            <TabsContent value="all" className="space-y-6 mt-4">
              {!hasSearchQuery && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                  <Star className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <span>Popular & Essential Items</span>
                </div>
              )}
              
              {formElements.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> 
                    {hasSearchQuery ? 'TAX FORMS' : 'POPULAR TAX FORMS'}
                  </h3>
                  <div className="space-y-2">
                    {formElements.slice(0, hasSearchQuery ? 10 : 4)}
                  </div>
                </div>
              )}
              
              {writeoffElements.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> 
                    {hasSearchQuery ? 'WRITE-OFFS' : 'COMMON WRITE-OFFS'}
                  </h3>
                  <div className="space-y-2">
                    {writeoffElements.slice(0, hasSearchQuery ? 10 : 6)}
                  </div>
                </div>
              )}
              
              {documentElements.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" /> 
                    {hasSearchQuery ? 'BUSINESS DOCUMENTS' : 'ESSENTIAL DOCUMENTS'}
                  </h3>
                  <div className="space-y-2">
                    {documentElements.slice(0, hasSearchQuery ? 10 : 6)}
                  </div>
                </div>
              )}
              
              {hasSearchQuery && totalResults === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No results found for "{searchQuery}"
                </div>
              )}
            </TabsContent>

            {/* Forms Tab */}
            <TabsContent value="forms" className="space-y-2 mt-4">
              {formElements.length > 0 ? formElements : (
                <div className="text-center py-8 text-muted-foreground">
                  {hasSearchQuery ? 'No tax forms found' : 'No popular forms available'}
                </div>
              )}
            </TabsContent>

            {/* Write-Offs Tab */}
            <TabsContent value="writeoffs" className="space-y-2 mt-4">
              {writeoffElements.length > 0 ? writeoffElements : (
                <div className="text-center py-8 text-muted-foreground">
                  {hasSearchQuery ? 'No write-offs found' : 'No popular write-offs available'}
                </div>
              )}
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="space-y-2 mt-4">
              {documentElements.length > 0 ? documentElements : (
                <div className="text-center py-8 text-muted-foreground">
                  {hasSearchQuery ? 'No documents found' : 'No documents available'}
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
